import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';

import Project from 'src/app/models/Project';
import { ProjectService } from '../project.service';

@Component({
    selector: 'app-jira-missing-projects-modal',
    templateUrl: './jira-missing-projects-modal.component.html',
    styleUrls: ['./jira-missing-projects-modal.component.scss'],
})
export class JiraMissingProjectsModalComponent {
    @Input() missingProjects: Array<{ projectName: string; projectKey: string; projectId?: string | number }> = [];
    @Input() jiraProjects: Array<{ projectName?: string; projectKey?: string; projectId?: string | number }> = [];
    @Input() companyUuid: string;
    @Output() refreshRequested = new EventEmitter<void>();

    creatingByKey: Record<string, boolean> = {};

    constructor(
        public activeModal: NgbActiveModal,
        private projectService: ProjectService,
        private toastr: ToastrService,
        private logger: NGXLogger,
    ) {}

    addProjectFromJira(missingProject: { projectName: string; projectKey: string; projectId?: string | number }): void {
        const jiraKey = (missingProject?.projectKey || '').toString().trim().toUpperCase();
        if (!jiraKey) {
            this.toastr.error('Jira key ontbreekt voor dit project.');
            return;
        }

        const jiraProjectId = this.resolveJiraProjectId(missingProject);
        if (!jiraProjectId) {
            this.toastr.error(`Geen Jira project-ID gevonden voor ${jiraKey}.`);
            return;
        }

        this.creatingByKey[jiraKey] = true;

        const payload: Project = {
            title: (missingProject?.projectName || jiraKey || 'New Jira Project').toString().trim(),
            projectType: 3,
            startDate: this.todayAsIsoDate(),
            endDate: null,
            present: true,
            billable: true,
            warranty: false,
            maintenanceContract: false,
            companyUuid: this.companyUuid,
        };
        (payload as any).jiraProjectKey = jiraProjectId;

        this.projectService.addProject(payload).subscribe({
            next: () => {
                this.toastr.success(`Project ${payload.title} aangemaakt.`);
                this.missingProjects = (this.missingProjects || []).filter((project) => this.normalizeJiraKey(project?.projectKey) !== jiraKey);
                this.refreshRequested.emit();
                this.creatingByKey[jiraKey] = false;
            },
            error: (error) => {
                this.logger.error('Failed to create project from Jira', error);
                const backendMessage = error?.error?.message || error?.error || error?.message;
                const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                    ? backendMessage
                    : 'Project aanmaken vanuit Jira mislukt.';
                this.toastr.error(message);
                this.creatingByKey[jiraKey] = false;
            },
        });
    }

    isCreating(projectKey: string): boolean {
        const key = this.normalizeJiraKey(projectKey);
        return !!this.creatingByKey[key];
    }

    private resolveJiraProjectId(missingProject: { projectName: string; projectKey: string; projectId?: string | number }): number {
        const direct = Number(missingProject?.projectId);
        if (!Number.isNaN(direct) && direct > 0) {
            return direct;
        }

        const projectKey = this.normalizeJiraKey(missingProject?.projectKey);
        const fromList = (this.jiraProjects || []).find((project) => this.normalizeJiraKey(project?.projectKey) === projectKey);
        const mapped = Number(fromList?.projectId);
        return !Number.isNaN(mapped) && mapped > 0 ? mapped : 0;
    }

    private todayAsIsoDate(): string {
        return new Date().toISOString().slice(0, 10);
    }

    private normalizeJiraKey(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim().toUpperCase();
    }
}
